import { Component, OnDestroy, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, fromEvent, Observable, Subject, takeUntil } from 'rxjs';
import { Area } from 'src/app/models/area.model';
import { ChecklistItem } from 'src/app/models/checklist-item.model';
import { SortByOptions, UserType } from 'src/app/models/customer.model';
import { DtStatsOptions, DTStatus } from 'src/app/models/dt.model';
import { KeyValueItem } from 'src/app/models/keyValueItem.model';
import { Region } from 'src/app/models/region.model';
import { RmuList, RmuStatsOptions } from 'src/app/models/rmu.model';
import { PermissionsEnum, SystemComponents } from 'src/app/models/role.model';
import { UserForFilter } from 'src/app/models/user.model';
import { AreaService } from 'src/app/services/area.service';
import { PermissionsService } from 'shared-lib';
import { RegionService } from 'src/app/services/region.service';
import { RmuService } from 'src/app/services/rmu.service';
import { TranslationService } from 'src/app/services/translation.service';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-rmus',
  templateUrl: './rmus.component.html',
  styleUrls: ['./rmus.component.scss']
})
export class RmusComponent implements OnInit, OnDestroy {
  buName: string = this.translationService.getByKeyFromStorage('BU');
  utName: string = this.translationService.getByKeyFromStorage('UT');
  rmus: RmuList[] = [];
  stats: KeyValueItem[] = [];

  mapSortBy: Record<number, string> = {
    [SortByOptions.ID]: 'COMMON.SORT_BY.ID',
    [SortByOptions.NEWEST]: 'COMMON.SORT_BY.NEWEST',
    [SortByOptions.SLRN_ASC]: 'COMMON.SORT_BY.SLRN_ASC',
    [SortByOptions.SLRN_DESC]: 'COMMON.SORT_BY.SLRN_DESC',
  };

  count: number = 2;
  pageSize: number = 10;
  currentPage: number = 1;
  filterByValue: string = '';

  utilityId: number = 0;
  regions: Region[] = [];
  areas: Area[] = [];
  selectedRegions: number[] = [];
  allAreas: Area[] = [];
  selectedAreas: number[] = [];

  usersFilter: any; // Stores value of filter
  mobileUsers: UserForFilter[] = []; // Store only mobile users
  adminUsers: UserForFilter[] = []; // Store only admin users
  allUsers: UserForFilter[] = []; // Store mobile or admin users depending on filter
  filteredUsers: UserForFilter[] = []; // Used for display and for filtering users list
  selectedUsers: number[] = [];
  selectedFilters: ChecklistItem[] = [];
  dateFrom: string;
  dateTo: string;
  searchText: string = '';
  searchTextUpdate = new Subject<string>();

  userType?: number;
  userTypeOptions = [
    {
      name: 'First Captured User',
      value: UserType.FirstCapturedUser,
      translation: 'COMMON.USER.TYPE.FIRST_CAPTURED',
    },
    {
      name: 'Last Captured User',
      value: UserType.LastCapturedUser,
      translation: 'COMMON.USER.TYPE.LAST_CAPTURED',
    },
    {
      name: 'Reassigned By',
      value: UserType.ReassignedBy,
      translation: 'COMMON.USER.TYPE.REASSIGNED',
    },
    {
      name: 'Validated By',
      value: UserType.ValidatedBy,
      translation: 'COMMON.USER.TYPE.VALIDATED',
    },
    {
      name: 'Audited By',
      value: UserType.AuditedBy,
      translation: 'COMMON.USER.TYPE.AUDITED',
    },
  ];

  rmuType: string = '';
  rmuTypeOptions = [
    { name: 'Gas-insulated', value: 'Gas-insulated', translation: 'RMU.TYPE_OF_RMU.GAS_INSULATED' },
    { name: 'Air-insulated', value: 'Air-insulated', translation: 'RMU.TYPE_OF_RMU.AIR_INSULATED' },
    { name: 'Vacuum', value: 'Vacuum', translation: 'RMU.TYPE_OF_RMU.VACUUM' }
  ];

  rmuStatus?: number;
  rmuStatusOptions = [
    {
      name: 'Active',
      value: DTStatus.Active,
      translation: 'TRANSFORMERS.DT_STATUS_OPTIONS.ACTIVE',
    },
    {
      name: 'Inactive',
      value: DTStatus.Inactive,
      translation: 'TRANSFORMERS.DT_STATUS_OPTIONS.INACTIVE',
    },
    {
      name: 'Suspended',
      value: DTStatus.Suspended,
      translation: 'TRANSFORMERS.DT_STATUS_OPTIONS.SUSPENDED',
    },
    {
      name: 'Decommissioned',
      value: DTStatus.Decommissioned,
      translation: 'TRANSFORMERS.DT_STATUS_OPTIONS.DECOMMISSIONED',
    }
  ];

  status: number = 0;
  statusOptions = [
    { name: 'Pending', value: 1, translation: 'COMMON.ASSET_STATUS.PENDING' },
    { name: 'Complete', value: 2, translation: 'COMMON.ASSET_STATUS.COMPLETE' },
    { name: 'Assigned', value: 4, translation: 'COMMON.ASSET_STATUS.ASSIGNED' },
    { name: 'Rejected', value: 5, translation: 'COMMON.ASSET_STATUS.REJECTED' },
    { name: 'Retagged', value: 6, translation: 'COMMON.ASSET_STATUS.RETAGGED' },
  ];

  selectedDateType: string;
  selectedDateTypeDisplay: string;
  showSelectDateType: boolean = false;
  searchFilterApplied: boolean = false;
  showSortBy: boolean = false;
  showUpload: boolean = false;
  clicked: boolean = false;
  allCheckboxesChecked: boolean = false;
  private ngUnsubscribe = new Subject<void>();
  clickObservable: Observable<Event> = fromEvent(document, 'click');

  sortByValue: SortByOptions = SortByOptions.NEWEST;
  sortByOptions = SortByOptions;
  sortByName: string = this.mapSortBy[SortByOptions.NEWEST];

  dateTypes = [
    {
      name: 'LastUpdateTime',
      value: 'Last Update Time',
      translation: 'COMMON.DATE_TYPE.LAST_UPDATE',
    },
    {
      name: 'TaggedDate',
      value: 'Tagged Date',
      translation: 'COMMON.DATE_TYPE.TAGGED',
    },
    {
      name: 'ValidatedDate',
      value: 'Validated Date',
      translation: 'COMMON.DATE_TYPE.VALIDATED',
    },
  ];
  constructor(
      public permissionsService: PermissionsService,
      private toastr: ToastrService,
      private regionService: RegionService,
      private areaService: AreaService,
      private translationService: TranslationService,
      private userService: UserService,
      private rmuService: RmuService
    ) {
      }

  ngOnInit(): void {
    this.utilityId = parseInt(localStorage.getItem('utilityId') || '');
    this.setFilters();
    this.getRegions();
    this.setCheckedUsers();
    this.getData();
    this.subscribeToClickEvent();

    this.searchTextUpdate.pipe(debounceTime(500)).subscribe((_) => {
      if (this.searchText == '') this.search();
      this.searchFilterApplied = false;
    });

    this.selectedDateType = this.selectedDateType ?? this.dateTypes[0].name;
    this.selectedDateTypeDisplay =
      this.selectedDateTypeDisplay ?? this.dateTypes[0].translation;
  }

  ngOnDestroy(): void {
    this.searchTextUpdate.complete();
  }

  private subscribeToClickEvent() {
    this.clickObservable
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((e: any) => {
        this.showUpload = false;
        this.showSortBy = false;
        this.showSelectDateType = false;
      });
  }
    
  private getData(): void {
    this.getRmuStats();
    this.reloadTable();
  }

  private getRmuStats() {
    var filter = this.getFilterObject();
    this.getTotalStats(filter);
    this.getRetaggedStats(filter);
    this.getDtStats(filter);
    this.getFeeederStats(filter);
  }

  private getTotalStats(obj: any) {
    var filter = {
      filter: obj,
      option: RmuStatsOptions.Total,
    };
    this.rmuService.getRmuStats(filter).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 200 || data?.status === 'OK') {
          var stat = this.stats.find(
            (x) => x.key == data.data.statType?.toString()!
          );
          if (stat) {
            stat.value = data.data.count!;
          } else {
            this.addStat(data.data.statType?.toString()!, data.data.count!);
          }
        } else {
          this.toastr.error(data?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getRetaggedStats(obj: any) {
    var filter = {
      filter: obj,
      option: RmuStatsOptions.Retagged,
    };
    this.rmuService.getRmuStats(filter).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 200 || data?.status === 'OK') {
          var stat = this.stats.find(
            (x) => x.key == data.data.statType?.toString()!
          );
          if (stat) {
            stat.value = data.data.count!;
          } else {
            this.addStat(data.data.statType?.toString()!, data.data.count!);
          }
        } else {
          this.toastr.error(data?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getDtStats(obj: any) {
    var filter = {
      filter: obj,
      option: RmuStatsOptions.NumberOfDts,
    };
    this.rmuService.getRmuStats(filter).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 200 || data?.status === 'OK') {
          var stat = this.stats.find(
            (x) => x.key == data.data.statType?.toString()!
          );
          if (stat) {
            stat.value = data.data.count!;
          } else {
            this.addStat(data.data.statType?.toString()!, data.data.count!);
          }
        } else {
          this.toastr.error(data?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getFeeederStats(obj: any) {
    var filter = {
      filter: obj,
      option: RmuStatsOptions.NumberOfFeeders,
    };
    this.rmuService.getRmuStats(filter).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 200 || data?.status === 'OK') {
          var stat = this.stats.find(
            (x) => x.key == data.data.statType?.toString()!
          );
          if (stat) {
            stat.value = data.data.count!;
          } else {
            this.addStat(data.data.statType?.toString()!, data.data.count!);
          }
          this.getF33Stats(obj);
          this.getF11Stats(obj);
        } else {
          this.toastr.error(data?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getF33Stats(obj: any) {
    var filter = {
      filter: obj,
      option: RmuStatsOptions.NumberOfFeeders33,
    };
    this.rmuService.getRmuStats(filter).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 200 || data?.status === 'OK') {
          var feederStat = this.stats.find((x) => x.key == 'NumberOfFeeders');
          var visitedItem = new KeyValueItem();
          visitedItem.friendlyName = 'RMU.FEEDERS33_STAT';
          visitedItem.isClickable = false;
          visitedItem.key = RmuStatsOptions.NumberOfFeeders33.toString();
          visitedItem.value = data.data.count;

          if (feederStat) {
            if(!feederStat.items) {
              feederStat.items = [];
            }
            feederStat.items.push(visitedItem);
          }
        } else {
          this.toastr.error(data?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getF11Stats(obj: any) {
    var filter = {
      filter: obj,
      option: RmuStatsOptions.NumberOfFeeders11,
    };
    this.rmuService.getRmuStats(filter).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 200 || data?.status === 'OK') {
          var feederStat = this.stats.find((x) => x.key == 'NumberOfFeeders');
          var visitedItem = new KeyValueItem();
          visitedItem.friendlyName = 'RMU.FEEDERS11_STAT';
          visitedItem.isClickable = false;
          visitedItem.key = RmuStatsOptions.NumberOfFeeders11.toString();
          visitedItem.value = data.data.count;

          if (feederStat) {
            if(!feederStat.items) {
              feederStat.items = [];
            }
            feederStat.items.push(visitedItem);
          }
        } else {
          this.toastr.error(data?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private addStat(key: string, value: number) {
    const newItem = new KeyValueItem();
    newItem.key = key;
    newItem.value = value;

    var keyType: RmuStatsOptions =
      RmuStatsOptions[key as keyof typeof RmuStatsOptions];
    switch (keyType) {
      case RmuStatsOptions.Total:
        newItem.order = 1;
        newItem.friendlyName = 'COMMON.TOTAL';
        break;
      case RmuStatsOptions.Retagged:
        newItem.order = 2;
        newItem.friendlyName = 'COMMON.ASSET_STATUS.RETAGGED';
        break;
      case RmuStatsOptions.NumberOfDts:
        newItem.order = 3;
        newItem.friendlyName = 'RMU.DT_STAT';
        newItem.isClickable = false;
        break;
      case RmuStatsOptions.NumberOfFeeders:
        newItem.order = 4;
        newItem.friendlyName = 'RMU.FEEDERS_STAT';
        newItem.isClickable = false;
        break;
    }

    this.stats.push(newItem);
    if (this.stats.length == 4)
      this.stats.sort((a, b) =>
        a.order < b.order ? -1 : a.order > a.order ? 1 : 0
      );
  }

  private getRegions() {
    this.regionService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.regions = responseData.data;
          this.setCheckedRegions();
          this.getAreas();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  search() {
    this.searchFilterApplied = this.searchText ? true : false;
    this.createFilterCards();
  }

  onFilterBy(option: any) {
    this.filterByValue = option;
    this.reloadTable(1);
  }

  reloadTable(page: any = null) {
    this.allCheckboxesChecked = false;
    if (page) this.currentPage = page;
    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count,
      },
      filterParams: this.getFilterObject(),
      utilityId: this.utilityId,
    };
 
    this.getAll(obj);
  }

  private getAll(obj: any) {
    this.rmuService.getAll(obj).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.rmus = responseData.data.data;
 
          this.count = responseData.data.count ?? this.count;
          if (responseData.message != '') {
            this.toastr.warning(responseData.message);
          }
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }

  download() {
    let obj = this.getFilterObject();

    this.rmuService.download(obj).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else if (data?.status === 'Pending' || data?.status === '600') {
          this.toastr.info(data?.message || '');
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  setCheckedRegions() {
    var filter = localStorage.getItem('filterRmus');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'RMU') return;
    if (filterObject.filter.filterParams.selectedRegions) {
      this.selectedRegions = filterObject.filter.filterParams.selectedRegions;
      this.regions.forEach((element) => {
        if (this.selectedRegions.includes(element.id)) element.checked = true;
        else element.checked = false;
      });
    }
  }

  setCheckedAreas() {
    var filter = localStorage.getItem('filterRmus');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'RMU') return;
    if (filterObject.filter.filterParams.selectedAreas) {
      this.selectedAreas = filterObject.filter.filterParams.selectedAreas;
      this.areas.forEach((element) => {
        if (this.selectedAreas.includes(element.id)) element.checked = true;
        else element.checked = false;
      });
    }
  }

  async setCheckedUsers() {
    var filter = localStorage.getItem('filterRmus');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'RMU') return;
    if (
      filterObject.filter.filterParams.selectedUsers &&
      filterObject.filter.filterParams.userType
    ) {
      this.userType = filterObject.filter.filterParams.userType;
      this.getUsersForFilter(true, filterObject);
    }
  }

  private getAreas() {
    this.areaService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.allAreas = responseData.data;
          this.areas = this.allAreas.filter((area) =>
            this.regions.some(
              (region) => region.id == area.regionId && region.checked
            )
          );
          this.setCheckedAreas();
          this.createFilterCards();
          this.removeFilterStorage();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  createFilterCards() {
    this.selectedFilters = [];
    let selectedUsers = this.allUsers.filter((x) => x.checked);
    let selectedAreas = this.areas.filter((x) => x.checked);
    this.selectedAreas = this.areas.filter((x) => x.checked).map((x) => x.id);

    this.selectedUsers = this.allUsers
      .filter((x) => x.checked)
      .map((x) => x.id);
    this.selectedRegions = this.regions
      .filter((x) => x.checked)
      .map((x) => x.id);

    //areas
    for (var i = 0; i < selectedAreas.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem(
          this.utName,
          selectedAreas[i].name,
          'COMMON.UT.SINGLE'
        )
      );
    }

    if (this.userType) {
      var userTypeName = UserType[this.userType!];
      var userTypeTranslationKey = this.userTypeOptions.find(
        (option) => option.value == this.userType
      )?.translation;
      this.selectedFilters.push(
        new ChecklistItem(
          'User type',
          userTypeName,
          'COMMON.USER.TYPE.TITLE',
          userTypeTranslationKey
        )
      );
    }

    //users
    for (var i = 0; i < selectedUsers.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem('User', selectedUsers[i].name, 'COMMON.USER.SINGLE')
      );
    }

    if (this.status != 0) {
      let status = this.statusOptions.filter((x) => x.value == this.status)[0];
      this.selectedFilters.push(
        new ChecklistItem(
          'Status',
          status.name,
          'COMMON.STATUS',
          status.translation
        )
      );
    }
    if (this.rmuType) {
      let type = this.rmuTypeOptions.filter((x) => x.value == this.rmuType)[0];
      this.selectedFilters.push(
        new ChecklistItem(
          'Type',
          type.name,
          'RMU.TYPE',
          type.translation
        )
      );
    }

    if (this.rmuStatus) {
      let type = this.rmuStatusOptions.filter((x) => x.value == this.rmuStatus)[0];
      this.selectedFilters.push(
        new ChecklistItem(
          'RmuStatus',
          type.name,
          'RMU.STATUS',
          type.translation
        )
      );
    }

    if (this.dateFrom) {
      this.selectedFilters.push(
        new ChecklistItem(
          'Date From',
          this.dateFrom.toString(),
          'COMMON.DATE_FROM'
        )
      );
    }
    if (this.dateTo) {
      this.selectedFilters.push(
        new ChecklistItem('Date To', this.dateTo.toString(), 'COMMON.DATE_TO')
      );
    }

    if (this.searchText) {
      this.selectedFilters.push(
        new ChecklistItem('Search', this.searchText, 'COMMON.SEARCH')
      );
    }
  }

  removeFilterStorage() {
    if (
      this.allAreas.length > 0 &&
      this.regions.length > 0
    )
      localStorage.removeItem('filterRmus');
  }

  getUsersForFilter(isFromCache: boolean, filterObject?: any) {
      if (
        this.userType == UserType.FirstCapturedUser ||
        this.userType == UserType.LastCapturedUser
      ) {
        if (this.mobileUsers.length === 0) {
          this.userService
            .getUsersForFilter(SystemComponents.Mobile)
            .subscribe((resp) => {
              this.mobileUsers = resp.data;
              this.allUsers = this.filteredUsers = this.mobileUsers;
              if (isFromCache) {
                this.selectedUsers =
                  filterObject.filter.filterParams.selectedUsers;
                this.allUsers.forEach((element) => {
                  if (this.selectedUsers.includes(element.id))
                    element.checked = true;
                  else element.checked = false;
                });
              }
              this.createFilterCards();
            });
        } else {
          this.allUsers = this.filteredUsers = [...this.mobileUsers];
        }
      } else {
        let permission = PermissionsEnum.Transformers_Assign;
  
        switch (Number(this.userType)) {
          case UserType.ValidatedBy:
            permission = PermissionsEnum.Transformers_ApproveReject;
            break;
          case UserType.AuditedBy:
            permission = PermissionsEnum.Transformers_AuditReview;
            break;
        }
  
        if (this.adminUsers.length === 0) {
          this.userService
            .getUsersForFilter(SystemComponents.Admin)
            .subscribe((resp) => {
              this.adminUsers = resp.data;
              this.allUsers = this.filteredUsers = this.adminUsers.filter((x) =>
                x.permissions?.some((x) => Number(x) == permission)
              );
              if (isFromCache) {
                this.selectedUsers =
                  filterObject.filter.filterParams.selectedUsers;
                this.allUsers.forEach((element) => {
                  if (this.selectedUsers.includes(element.id))
                    element.checked = true;
                  else element.checked = false;
                });
                this.createFilterCards();
              }
            });
        } else {
          this.allUsers = this.filteredUsers = [
            ...this.adminUsers.filter((x) =>
              x.permissions?.some((x) => Number(x) == permission)
            ),
          ];
        }
      }
  }

  datesValid() {
    if (this.dateFrom && this.dateTo && this.dateTo?.toString() != '') {
      const valid: boolean =
        new Date(this.dateFrom).getTime() <
        new Date(`${this.dateTo.toString()}T23:59:59`).getTime();
      return valid;
    }
    return true;
  }

  selectDateType(dateType: any) {
    this.selectedDateType = dateType.name;
    this.selectedDateTypeDisplay = dateType.translation;
    this.showSelectDateType = false;
  }

  sortBy(option: SortByOptions) {
    this.sortByValue = option;
    this.sortByName = this.mapSortBy[option];
  }

  getFilterObject() {
    let obj = {
      sortBy: this.sortByValue,
      search: this.searchText,
      filterBy: this.filterByValue,
      dateFrom: this.dateFrom ? this.dateFrom : null,
      dateTo: this.dateTo ? `${this.dateTo}T23:59:59` : null,
      dateType: this.selectedDateType,
      selectedRegions: this.selectedRegions,
      selectedAreas: this.selectedAreas,
      userType: this.userType
        ? this.selectedUsers.length > 0
          ? this.userType
          : null
        : null,
      selectedUsers: this.selectedUsers.length > 0 ? this.selectedUsers : null,
      status: this.status,
      selectedIds: this.rmus.filter((x) => x.checked == true).map((x) => x.id),
      rmuType: this.rmuType,
      rmuStatus: this.rmuStatus
    };
    return obj;
  }

  setFilters() {
    var filter = localStorage.getItem('filterRmus');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'RMU') return;
    this.currentPage = filterObject.filter.pageInfo.page;
    this.pageSize = filterObject.filter.pageInfo.pageSize;
    this.selectedUsers = filterObject.filter.filterParams.selectedUsers
      ? filterObject.filter.filterParams.selectedUsers
      : [];
    this.count = filterObject.filter.pageInfo.count;
    this.sortByValue = filterObject.filter.filterParams.sortBy;
    this.searchText = filterObject.filter.filterParams.search;
    this.status = filterObject.filter.filterParams.status;
    this.selectedAreas = filterObject.filter.filterParams.selectedAreas
      ? filterObject.filter.filterParams.selectedAreas
      : [];
    this.selectedRegions = filterObject.filter.filterParams.selectedRegions
      ? filterObject.filter.filterParams.selectedRegions
      : [];
    this.sortByName = this.mapSortBy[this.sortByValue];
    this.dateFrom = filterObject.filter.filterParams.dateFrom;
    this.dateTo = filterObject.filter.filterParams.dateTo;

    this.selectedDateType =
      filterObject.filter.filterParams.dateType ?? this.dateTypes[0].name;
    this.selectedDateTypeDisplay =
      filterObject.filter.filterParams.dateType ??
      this.dateTypes[0].translation;
    this.rmuType = filterObject.filter.filterParams.rmuType;
    this.rmuStatus = filterObject.filter.filterParams.rmuStatus;
  }

  selectRegion(e: any) {
    this.areas = this.allAreas.filter((area) =>
      this.regions.some(
        (region) => region.id == area.regionId && region.checked
      )
    );
    this.allAreas.forEach((area) => {
      if (!this.areas.some((a) => a.id === area.id)) {
        area.checked = false; //uncheck areas for unchecked regions
      }
    });

    this.createFilterCards();
  }
  selectArea(e: any) {
    this.createFilterCards();
  }
  selectStatus(e: any) {
    this.status = +e.target.value;
    this.createFilterCards();
  }

  selectUserType(e: any) {
    if (this.userType) this.resetUserList();

    if (e.target.value != '') {
      this.userType = e.target.value;
      this.getUsersForFilter(false);
    } else {
      this.userType = undefined;
    }

    this.createFilterCards();
  }

  selectRmuType(e: any) {
    this.rmuType = e.target.value;
    this.createFilterCards();
  }

  selectRmuStatus(e: any) {
    this.rmuStatus = +e.target.value;
    this.createFilterCards();
  }

  resetUserList() {
    this.allUsers.forEach((user) => {
      user.checked = false;
    });
    this.allUsers = [];
  }

  selectUsers(e: any) {
    if (e.status) {
      this.allUsers.push(e.value);
    } else {
      var index = this.allUsers.indexOf(e.value);
      this.allUsers.splice(index, 1);
    }
    this.createFilterCards();
  }

  applySearchFilter(e: any) {
    this.filteredUsers = this.allUsers.filter((user) =>
      user.name.toLocaleLowerCase().includes(e.toLocaleLowerCase())
    );
  }

  resetFilter(e: any) {
    this.selectedFilters = [];

    this.regions.forEach((region) => {
      region.checked = false;
    });
    this.areas.forEach((area) => {
      area.checked = false;
    });
    this.areas = [];
    this.allUsers.forEach((user) => {
      user.checked = false;
    });
    this.allUsers = this.filteredUsers = [...[]];
    this.selectedAreas = [];
    this.selectedRegions = [];
    this.userType = undefined;
    this.status = 0;
    this.searchText = '';
    this.dateFrom = '';
    this.dateTo = '';
    this.rmuType = '';
    this.rmuStatus = undefined;
    this.clicked = !this.clicked;

    this.reloadTable(1);
    this.getRmuStats();
  }

  applyFilter() {
    this.reloadTable(1);
    this.getRmuStats();
  }

  removeFilter(item: ChecklistItem) {
    if (item.property == this.utName) {
      this.areas.forEach((area) => {
        if (area.name === item.selectedValue) {
          area.checked = false;
        }
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'User') {
      this.allUsers.forEach((user) => {
        if (user.name === item.selectedValue) {
          user.checked = false;
        }
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'User type') {
      this.userType = undefined;
      this.allUsers.forEach((user) => {
        user.checked = false;
      });
      this.allUsers = this.filteredUsers = [...[]];
    } else if (item.property == 'Status') {
      this.status = 0;
    } else if (item.property == 'Date From') {
      this.dateFrom = '';
    } else if (item.property == 'Date To') {
      this.dateTo = '';
    } else if (item.property == 'Search') {
      this.searchText = '';
    } else if (item.property == 'Type') {
      this.rmuType = '';
    } else if (item.property == 'RmuStatus') {
      this.rmuStatus = undefined;
    }

    this.createFilterCards();

    if (this.selectedFilters.length == 0) {
      this.regions.forEach((region) => {
        region.checked = false;
      });
      this.reloadTable(1);
      this.getRmuStats();
    }
  }

  checkAllCheckboxes() {
    this.rmus.forEach((x) => (x.checked = !this.allCheckboxesChecked));
    this.allCheckboxesChecked = !this.allCheckboxesChecked;
  }

  setFilterStorage() {
    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count,
      },
      filterParams: this.getFilterObject(),
      utilityId: this.utilityId,
    };

    // Remove 'T23:59:59' from the dateTo to avoid errors
    if (obj.filterParams.dateTo) {
      obj.filterParams.dateTo = obj.filterParams.dateTo.replace(
        'T23:59:59',
        ''
      );
    }

    localStorage.setItem(
      'filterTransformer',
      JSON.stringify({ type: 'Transformer', filter: obj })
    );
  }

  checkRmu(id: number) {
    const dt = this.rmus.find((x) => x.id === id);
    if (dt) {
      dt.checked = !dt.checked;
      this.areAllCheckboxesChecked();
    }
  }

  areAllCheckboxesChecked() {
    this.allCheckboxesChecked = this.rmus.every((x) => x.checked);
  }

  pageChange(value: any) {
    this.currentPage = value;
    this.reloadTable();
  }
}
